import type { DraftPlan } from "./types";

/**
 * Pre-save destructive-edit detection (Tech-decision "Destructive-edit guard", R5/FL-5). Diffs the
 * draft about to be saved against the loaded server base and lists every destructive change: a table
 * removed or retired, a whole zone removed, a standing zone's capacity reduced. This drives a BLOCKING
 * confirm dialog so a manager decides BEFORE the write — never a save-then-toast ambush.
 *
 * Deliberately NOT a booking-count computation: the task rejects duplicating the model's assignment
 * logic client-side (it races live bookings). The authoritative "N future bookings will be re-flagged"
 * count comes from the model's impact endpoint once landed; here the guard names WHAT is being removed
 * and warns that any future bookings on it are re-flagged server-side (FloorPlanChanged → refit).
 */

export type ImpactKind =
  | "TABLE_REMOVED"
  | "TABLE_RETIRED"
  | "ZONE_REMOVED"
  | "ZONE_CAPACITY_REDUCED";

export interface ImpactItem {
  kind: ImpactKind;
  /** The affected element's id. */
  id: string;
  /** A human label (table label / zone name) for the dialog line. */
  label: string;
  /** For ZONE_CAPACITY_REDUCED: the previous and requested capacity. */
  from?: number | null;
  to?: number | null;
}

export function computeImpact(base: DraftPlan, draft: DraftPlan): ImpactItem[] {
  const items: ImpactItem[] = [];

  const draftTableById = new Map(draft.tables.map((t) => [t.id, t]));
  for (const b of base.tables) {
    if (b.retired) continue; // already retired on the server — no new impact
    const d = draftTableById.get(b.id);
    if (!d) {
      items.push({ kind: "TABLE_REMOVED", id: b.id, label: b.label });
    } else if (d.retired) {
      items.push({ kind: "TABLE_RETIRED", id: b.id, label: d.label });
    }
  }

  const draftZoneById = new Map(draft.zones.map((z) => [z.id, z]));
  for (const b of base.zones) {
    const d = draftZoneById.get(b.id);
    if (!d) {
      items.push({ kind: "ZONE_REMOVED", id: b.id, label: b.name });
      continue;
    }
    if (
      b.kind === "STANDING_HEADCOUNT" &&
      d.kind === "STANDING_HEADCOUNT" &&
      typeof b.capacity === "number" &&
      typeof d.capacity === "number" &&
      d.capacity < b.capacity
    ) {
      items.push({
        kind: "ZONE_CAPACITY_REDUCED",
        id: b.id,
        label: d.name,
        from: b.capacity,
        to: d.capacity,
      });
    }
  }

  return items;
}

export function hasDestructiveEdits(base: DraftPlan, draft: DraftPlan): boolean {
  return computeImpact(base, draft).length > 0;
}
